import { c as _c } from "react/compiler-runtime";
import { BinIcon, PlusIcon } from '@systemeio/ui/icons';
import { Checkbox, NeutralButton, NeutralTypeEnum, TimePicker, FieldErrorAndDescription } from '@systemeio/ui';
import { getWeekdayShortLocoKey, sortWeekdaySlots } from 'modules/calendar/utils/date-time-formatters';
import ButtonLikeLink from 'shared/components/button-like-link';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { twJoin, twMerge } from 'tailwind-merge';
import { AvailabilityErrors, AvailabilitySlot, AvailabilitySlotId, WeekdaysBackendEnum } from '../types/availability-interface';
import { AvailabilitySlotsSkeleton } from './availability-slots-skeleton';
interface WeekdaysAvailabilitySlotsProps {
  slots: AvailabilitySlot[];
  addSlot: (day: Omit<AvailabilitySlot, 'id'>) => void;
  updateSlot: (day: AvailabilitySlot) => void;
  removeSlots: (slotIds: AvailabilitySlotId[]) => void;
  isLoading?: boolean;
  disabled?: boolean;
  titleClassName?: string;
  errors?: AvailabilityErrors;
}
export function WeekdaysAvailabilitySlots(t0) {
  const $ = _c(24);
  const {
    slots,
    addSlot,
    updateSlot,
    removeSlots,
    isLoading,
    disabled,
    titleClassName,
    errors
  } = t0;
  const {
    t
  } = useLocoTranslation();
  let t1;
  if ($[0] !== titleClassName) {
    t1 = twMerge("mb-5", titleClassName);
    $[0] = titleClassName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("dashboard.calendar.weekly_hours_availability");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <p className={t1}>{t2}</p>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== isLoading) {
    t4 = isLoading && <AvailabilitySlotsSkeleton />;
    $[7] = isLoading;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== addSlot || $[10] !== disabled || $[11] !== errors || $[12] !== isLoading || $[13] !== removeSlots || $[14] !== slots || $[15] !== t || $[16] !== updateSlot) {
    t5 = !isLoading && Object.values(WeekdaysBackendEnum).map(weekday => {
      const weekdaySlots = slots.filter(t6 => {
        const {
          dayOfWeek
        } = t6;
        return dayOfWeek === weekday;
      });
      const isAvailable = weekdaySlots.length > 0;
      return <div key={weekday} className="border-text-secondary/30 grid grid-cols-12 items-start border-t py-5"><div className={twJoin("col-span-4 lg:col-span-2", weekdaySlots.length > 1 ? "self-start" : "self-center")}><Checkbox value={isAvailable} label={t(getWeekdayShortLocoKey(weekday))} onChange={() => {
            if (isAvailable) {
              removeSlots(weekdaySlots.map(_temp));
              return;
            }
            addSlot({
              dayOfWeek: weekday,
              startTime: null,
              endTime: null,
              date: null
            });
          }} disabled={disabled} /></div><div className="col-span-8 lg:col-span-10">{isAvailable ? <div className="grid grid-cols-[1fr_minmax(20px,50px)] items-center gap-2 lg:grid-cols-[1fr_160px]">{sortWeekdaySlots(weekdaySlots).map((slot, i) => {
              const globalIndex = slots.findIndex(s => s.id === slot.id);
              return <WeekdaySlot key={i} weekdaySlot={slot} addSlot={addSlot} removeSlots={removeSlots} updateSlot={updateSlot} slotIndex={i} weekday={weekday} disabled={disabled} startTimeError={errors?.[`weeklyDailySettings[${globalIndex}].startTime`]} endTimeError={errors?.[`weeklyDailySettings[${globalIndex}].endTime`]} dateError={errors?.[`weeklyDailySettings[${globalIndex}].date`]} />;
            })}</div> : <div className="grid grid-cols-4 items-center gap-2"><span className="col-span-2">{t("dashboard.calendar.time_slot_unavailable")}</span><ButtonLikeLink type="button" className="col-span-2 ml-auto flex items-center gap-1 py-2 text-sm" disabled={true}><PlusIcon /><span className="hidden lg:block">{t("dashboard.calendar.add_time_slot")}</span></ButtonLikeLink></div>}</div></div>;
    });
    $[9] = addSlot;
    $[10] = disabled;
    $[11] = errors;
    $[12] = isLoading;
    $[13] = removeSlots;
    $[14] = slots;
    $[15] = t;
    $[16] = updateSlot;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t4 || $[19] !== t5) {
    t6 = <div className="flex flex-col">{t4}{t5}</div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t3 || $[22] !== t6) {
    t7 = <>{t3}{t6}</>;
    $[21] = t3;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
}
function _temp(t0) {
  const {
    id
  } = t0;
  return id;
}
interface WeekdaySlotProps extends Omit<WeekdaysAvailabilitySlotsProps, 'slots' | 'isLoading'> {
  weekdaySlot: AvailabilitySlot;
  slotIndex: number;
  weekday: WeekdaysBackendEnum;
  disabled?: boolean;
  startTimeError?: string;
  endTimeError?: string;
  dateError?: string;
}
function WeekdaySlot(t0) {
  const $ = _c(39);
  const {
    weekdaySlot,
    addSlot,
    removeSlots,
    updateSlot,
    slotIndex,
    weekday,
    disabled,
    startTimeError,
    endTimeError,
    dateError
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  let t1;
  if ($[0] !== updateSlot || $[1] !== weekdaySlot) {
    t1 = seconds => {
      updateSlot({
        ...weekdaySlot,
        startTime: seconds
      });
    };
    $[0] = updateSlot;
    $[1] = weekdaySlot;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = user?.dateFormat;
  const t3 = user?.timeFormat;
  let t4;
  if ($[3] !== disabled || $[4] !== startTimeError || $[5] !== t1 || $[6] !== t2 || $[7] !== t3 || $[8] !== weekdaySlot.startTime) {
    t4 = <TimePicker seconds={weekdaySlot.startTime} onChangeSeconds={t1} disabled={disabled} wrapperClassName="w-[115px] sm:w-[128px]" classNameCustomInput="w-full" userDateFormat={t2} userTimeFormat={t3} error={startTimeError} />;
    $[3] = disabled;
    $[4] = startTimeError;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = weekdaySlot.startTime;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== updateSlot || $[11] !== weekdaySlot) {
    t5 = seconds_0 => {
      updateSlot({
        ...weekdaySlot,
        endTime: seconds_0
      });
    };
    $[10] = updateSlot;
    $[11] = weekdaySlot;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = user?.dateFormat;
  const t7 = user?.timeFormat;
  let t8;
  if ($[13] !== disabled || $[14] !== endTimeError || $[15] !== t5 || $[16] !== t6 || $[17] !== t7 || $[18] !== weekdaySlot.endTime) {
    t8 = <TimePicker seconds={weekdaySlot.endTime} onChangeSeconds={t5} disabled={disabled} wrapperClassName="w-[115px] sm:w-[128px]" classNameCustomInput="w-full" userDateFormat={t6} userTimeFormat={t7} error={endTimeError} />;
    $[13] = disabled;
    $[14] = endTimeError;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = weekdaySlot.endTime;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t4 || $[21] !== t8) {
    t9 = <div className="xxl:gap-5 flex flex-row flex-wrap gap-1">{t4}{t8}</div>;
    $[20] = t4;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== dateError) {
    t10 = dateError && <FieldErrorAndDescription error={dateError} />;
    $[23] = dateError;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t9) {
    t11 = <div className="flex flex-1 flex-col">{t9}{t10}</div>;
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== addSlot || $[29] !== disabled || $[30] !== removeSlots || $[31] !== slotIndex || $[32] !== t || $[33] !== weekday || $[34] !== weekdaySlot.id) {
    t12 = <div className="ml-auto">{slotIndex === 0 ? <ButtonLikeLink type="button" className="ml-auto flex gap-1 pl-0 text-sm md:pl-4" onClick={() => {
        addSlot({
          dayOfWeek: weekday,
          startTime: null,
          endTime: null,
          date: null
        });
      }} disabled={disabled}><PlusIcon fill={disabled ? "gray" : "rgb(var(--brand-primary))"} /><span className="hidden lg:block">{t("dashboard.calendar.add_time_slot")}</span></ButtonLikeLink> : <NeutralButton type="button" neutralType={NeutralTypeEnum.danger} onClick={() => removeSlots([weekdaySlot.id])} disabled={disabled}><BinIcon width={16} height={16} className="fill-text-primary group-hover:fill-danger transition-all " /><span className="hidden lg:block">{t("global.remove")}</span></NeutralButton>}</div>;
    $[28] = addSlot;
    $[29] = disabled;
    $[30] = removeSlots;
    $[31] = slotIndex;
    $[32] = t;
    $[33] = weekday;
    $[34] = weekdaySlot.id;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t11 || $[37] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
}